import { create } from "zustand";
import { StateCreator } from "zustand";
import { supabase } from "../supabase/supabaseClient";
import { FileEntityType, SupabaseTableEnum } from "../supabase/supabaseTypes";
import { Logger } from "../logger/Logger";
import { getFileType } from "../utils/file-utils";

interface FileUrlCacheItem {
	id: string;
	file_name: string | null;
	file_type: string | null;
	path_name: string;
	signed_url: string;
	url_expires_at: number; // timestamp in milliseconds
	meta_data?: Record<string, any> | string | null;
}

interface FileContentItem {
	url: string;
	size: number;
}

export interface FileUrlCacheSlice {
	fileCache: Record<string, FileUrlCacheItem>;
	/** Downloaded file content as object URLs, so reopening a file does not download it again. */
	fileContent: Record<string, FileContentItem>;
	getFileIfCached: (fileId: string) => FileUrlCacheItem | null;
	getFile: (fileId: string) => Promise<FileUrlCacheItem | null>;
	preloadFiles: (fileIds: string[]) => Promise<void>;
	loadFileContent: (fileId: string) => Promise<string | null>;
	evictFiles: (fileIds: string[]) => void;
}

const URL_EXPIRY_BUFFER_MS = 5 * 60 * 1000; // 5 minute buffer before actual expiry
const SIGNED_URL_DURATION_SECONDS = 3600; // 1 hour
const MAX_CONTENT_BYTES = 200 * 1024 * 1024;

const initialState: Pick<FileUrlCacheSlice, "fileCache" | "fileContent"> = {
	fileCache: {},
	fileContent: {},
};

const pendingContent = new Map<string, Promise<string | null>>();

const createFileUrlCacheStore: StateCreator<
	FileUrlCacheSlice,
	[],
	[],
	FileUrlCacheSlice
> = (set, get) => ({
	...initialState,
	// Exclusively for getting files without having to async await
	getFileIfCached: (fileId: string) => {
		const cached = get().fileCache[fileId];
		if (cached && cached.url_expires_at > (Date.now() + URL_EXPIRY_BUFFER_MS)) {
			return cached;
		}
		return null;
	},
	getFile: async (fileId: string) => {
		const cached = get().fileCache[fileId];

		// Check if file is cached and not expired
		if (cached && cached.url_expires_at > (Date.now() + URL_EXPIRY_BUFFER_MS)) {
			return cached;
		}

		// If the file has never been cached, fetch it first from the db to get meta data
		let file: FileEntityType | null = null;
		if (!cached) {
			try {
				const { data: fileData, error: fileError } = await supabase
					.from(SupabaseTableEnum.FILES)
					.select("*")
					.eq("id", fileId)
					.single();

				if (fileError || !fileData) {
					Logger.error("Error fetching file details", {}, fileError);
					return null;
				}

				file = fileData as FileEntityType;
			} catch (error) {
				Logger.error("Error in refreshFileUrl", {}, error);
				return null;
			}
		}

		try {
			const pathName = cached?.path_name ?? file?.path_name;
			const metaData = cached?.meta_data ?? file?.meta_data;
			const fileName = cached?.file_name ?? file?.file_name ?? null;
			const fileType = getFileType(metaData as Record<string, any> | null, fileName);

			// Create signed URL
			const { data: urlData, error: urlError } = await supabase.storage
				.from("v1")
				.createSignedUrl(pathName, SIGNED_URL_DURATION_SECONDS);

			if (urlError || !urlData?.signedUrl) {
				Logger.error("Error creating signed URL", {}, urlError);
				return null;
			}

			// Calculate expiry time (1 hour minus buffer)
			const expiryTime =
				Date.now() +
				SIGNED_URL_DURATION_SECONDS * 1000 -
				URL_EXPIRY_BUFFER_MS;

			const fileItem: FileUrlCacheItem = {
				id: fileId,
				file_name: fileName,
				file_type: fileType,
				path_name: pathName,
				signed_url: urlData.signedUrl,
				url_expires_at: expiryTime,
				meta_data: metaData as Record<string, any> | null,
			};

			set((state) => ({
				fileCache: {
					...state.fileCache,
					[fileId]: fileItem,
				},
			}));

			return fileItem;
		} catch (error) {
			Logger.error("Error in refreshFileUrl", {}, error);
			return null;
		}
	},

	loadFileContent: (fileId: string) => {
		const cached = get().fileContent[fileId];
		if (cached) return Promise.resolve(cached.url);

		const pending = pendingContent.get(fileId);
		if (pending) return pending;

		const load = (async () => {
			const file = await get().getFile(fileId);
			// Videos stream from the signed URL instead of being held in memory.
			if (!file || file.file_type === "video") return null;
			try {
				const response = await fetch(file.signed_url);
				if (!response.ok) return null;
				const blob = await response.blob();
				const url = URL.createObjectURL(blob);
				set((state) => {
					// Oldest first (insertion order); evict until the new file fits.
					const fileContent = { ...state.fileContent };
					let total = Object.values(fileContent).reduce(
						(sum, item) => sum + item.size,
						blob.size
					);
					for (const [id, item] of Object.entries(fileContent)) {
						if (total <= MAX_CONTENT_BYTES) break;
						URL.revokeObjectURL(item.url);
						delete fileContent[id];
						total -= item.size;
					}
					fileContent[fileId] = { url, size: blob.size };
					return { fileContent };
				});
				return url;
			} catch (error) {
				Logger.error("Error loading file content", {}, error);
				return null;
			} finally {
				pendingContent.delete(fileId);
			}
		})();

		pendingContent.set(fileId, load);
		return load;
	},

	evictFiles: (fileIds: string[]) => {
		set((state) => {
			const newCache = { ...state.fileCache };
			const fileContent = { ...state.fileContent };
			fileIds.forEach((id) => {
				delete newCache[id];
				if (fileContent[id]) URL.revokeObjectURL(fileContent[id].url);
				delete fileContent[id];
			});
			return { fileCache: newCache, fileContent };
		});
	},

	preloadFiles: async (fileIds: string[]) => {
		// Filter out files that are already cached and not expired
		const now = Date.now();
		const fileIdsToLoad = fileIds.filter((id) => {
			const cached = get().fileCache[id];
			return !cached || cached.url_expires_at <= now;
		});

		if (fileIdsToLoad.length === 0) return;

		// Fetch all files in one query
		const { data: filesData, error: filesError } = await supabase
			.from(SupabaseTableEnum.FILES)
			.select("*")
			.in("id", fileIdsToLoad);

		if (filesError || !filesData) {
			Logger.error("Error fetching files for preload", {}, filesError);
			return;
		}

		// Create signed URLs for all files
		const urlPromises = filesData.map(async (file: FileEntityType) => {
			try {
				const { data: urlData, error: urlError } =
					await supabase.storage
						.from(file.bucket_name || "v1")
						.createSignedUrl(
							file.path_name,
							SIGNED_URL_DURATION_SECONDS
						);

				if (urlError || !urlData?.signedUrl) {
					Logger.error(
						"Error creating signed URL in preload",
						{},
						urlError
					);
					return null;
				}

				const fileType = getFileType(file.meta_data as Record<string, any> | null, file.file_name);

				const expiryTime =
					Date.now() +
					SIGNED_URL_DURATION_SECONDS * 1000 -
					URL_EXPIRY_BUFFER_MS;

				return {
					id: file.id,
					file_name: file.file_name,
					file_type: fileType,
					path_name: file.path_name,
					signed_url: urlData.signedUrl,
					url_expires_at: expiryTime,
					meta_data: file.meta_data,
				};
			} catch (error) {
				Logger.error("Error processing file in preload", {}, error);
				return null;
			}
		});

		const results = await Promise.all(urlPromises);
		const validResults = results.filter(
			(r) => r !== null
		) as FileUrlCacheItem[];

		// Update cache with all valid results
		if (validResults.length > 0) {
			set((state) => {
				const newCache = { ...state.fileCache };
				validResults.forEach((item) => {
					newCache[item.id] = item;
				});
				return { fileCache: newCache };
			});
		}
	},
});

export const useFileUrlCacheStore = create<FileUrlCacheSlice>(
	createFileUrlCacheStore
);
